import { Card, Space, Tag, Typography } from "antd";
import { stripMarkdown } from "../cardUtils";
import { CustomTooltip } from "src/components/CustomTooltip";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * TrendCard Component
 *
 * A card component for displaying trend data with metrics, timeseries,
 * and change summaries.
 *
 * @example
 * ```tsx
 * <TrendCard
 *   metric="PRs merged"
 *   trend="up"
 *   timeseries={[10, 15, 20, 25]}
 *   changeSummary="PRs increased due to infrastructure improvements"
 * />
 * ```
 */
export interface TrendIndicatorProps {
  /**
   * The metric being tracked (e.g., 'PRs merged', 'Issue resolution time')
   */
  metric: string;

  /**
   * The trend direction
   */
  trend: "up" | "down" | "flat" | "volatile";

  /**
   * Array of values representing the timeseries data
   */
  timeseries: number[];

  /**
   * Brief explanation of the change
   */
  changeSummary: string;

  /**
   * Optional CSS class name for custom styling
   */
  className?: string;

  /**
   * Layout variant for the card: 'default' (vertical), 'compact' (horizontal), 'minimal' (text-only)
   */
  variant?: "default" | "compact" | "minimal";
}

const getTrendColor = (trend: TrendIndicatorProps["trend"]) => {
  switch (trend) {
    case "up": {
      return "success";
    }
    case "down": {
      return "error";
    }
    case "volatile": {
      return "warning";
    }
    default: {
      return "default";
    }
  }
};

const getTrendIcon = (trend: TrendIndicatorProps["trend"]) => {
  switch (trend) {
    case "up": {
      return "↑";
    }
    case "down": {
      return "↓";
    }
    case "volatile": {
      return "↕";
    }
    default: {
      return "→";
    }
  }
};

export const TrendIndicator: React.FC<TrendIndicatorProps> = ({
  metric,
  trend,
  timeseries,
  changeSummary,
  className,
  variant = "default",
}) => {
  if (variant === "compact") {
    // Compact horizontal card
    return (
      <Card
        className={[className, "vzbl-trend-compact"].filter(Boolean).join(" ")}
        size="small"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontWeight: 600 }}>{metric}</span>
          <Tag color={getTrendColor(trend)}>
            {getTrendIcon(trend)} {trend}
          </Tag>
          <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
            {changeSummary}
          </Markdown>
        </div>
      </Card>
    );
  }
  if (variant === "minimal") {
    // Minimal, text-only card
    return (
      <div
        className={[className, "vzbl-trend-minimal"].filter(Boolean).join(" ")}
        style={{ padding: 8, fontSize: 14 }}
      >
        <strong>{metric}</strong>:
        <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
          {changeSummary}
        </Markdown>
      </div>
    );
  }
  // Default vertical card
  const latestValue = (timeseries && timeseries.at(-1)) ?? 0;
  const previousValue = (timeseries && timeseries.at(-2)) ?? 0;
  const percentChange = previousValue
    ? ((latestValue - previousValue) / previousValue) * 100
    : 0;
  return (
    <Card
      className={[className, "vzbl-trend-default"].filter(Boolean).join(" ")}
      size="small"
      style={{
        border: "solid 1px var(--color-foreground-quaternary)",
        borderRadius: "var(--border-radius-2)",
      }}
    >
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          <Typography.Text className="vzbl-header-3 single-line">
            <CustomTooltip content={stripMarkdown(metric)}>
              {stripMarkdown(metric)}
            </CustomTooltip>
          </Typography.Text>

          <Space size={8} style={{ flexWrap: "wrap", maxWidth: "100%" }}>
            <Tag className="vzbl-pill-sm" color={getTrendColor(trend)}>
              {getTrendIcon(trend)} {trend}
            </Tag>
            <Tag className="vzbl-pill-sm">
              {percentChange > 0 ? "+" : ""}
              {percentChange.toFixed(1)}%
            </Tag>
          </Space>
        </Space>

        <Typography.Text className="vzbl-text-3">
          <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
            {changeSummary}
          </Markdown>
        </Typography.Text>

        <div
          style={{
            height: 40,
            display: "flex",
            alignItems: "flex-end",
            gap: 4,
          }}
        >
          {timeseries &&
            timeseries.map((value, idx) => {
              const maxValue = Math.max(...timeseries);
              const height = (value / maxValue) * 100;
              return (
                <div
                  key={idx}
                  style={{
                    flex: 1,
                    height: `${height}%`,
                    backgroundColor: "var(--color-foreground-quaternary)",
                    borderRadius: "2px 2px 0 0",
                  }}
                />
              );
            })}
        </div>
      </Space>
    </Card>
  );
};
